import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import styled, { keyframes, css } from 'styled-components';
import { duration, easing, transition } from '../styles/motion';
import { fluidSheet, fluidScrim, fluidCascade, useLandWindow } from './Fluid'; // [FLUID-2 2026-10-12]
// [LIQUID-GLASS 2026-11-02, user] the same floating glass sheet, cards and buttons as App Alerts and the Bin
import {
  lgSurface, lgSurfaceShadow, lgCard, lgControl, lgControlBg, lgControlHover, lgPress, lgSquircle, lgHair, lgTopGlow,
  lgButtonPrimary, lgButtonSecondary, LG_PRESS, LgPressProps,
} from '../styles/liquidGlass';
import { SkeletonBox } from './Skeleton';
import { toastSuccess, toastError } from './toastService';
import { acceptNotificationSuggestion, cancelNotificationCard } from '../services/api'; // [ACCEPT-REFRESH 2026-09-17, user]
import { notifyDataChanged } from '../services/realtime'; // [ACCEPT-REFRESH 2026-09-17, user]

/* [PERF 2026-08-14] Locale formatters built ONCE — `toLocale*(locale, {options})`
 * constructs a fresh Intl formatter per call (measured 34us money / 90us date on V8,
 * vs 0.7us / 1.5us cached). Calls WITHOUT options are already fast and left alone. */
const MONEY_FMT = new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
// [DATE-STYLE 2026-09-29, user] "9/29/2026" → "Sep 29, 2026" (one date style across the app).
const MONTH_SHORT_DAY_YEAR_UTC_FMT = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });

// Completion animation: the card glides right off the panel and fades (so the
// user clearly sees "this one's handled"), then the vacated space collapses so
// the cards below ease up to fill it instead of snapping.
const cardSwipeOut = keyframes`
  0%   { transform: translateX(0);    opacity: 1; max-height: 600px; margin-bottom: 14px; }
  55%  { transform: translateX(120%); opacity: 0; max-height: 600px; margin-bottom: 14px; }
  100% { transform: translateX(120%); opacity: 0; max-height: 0;     margin-bottom: 0; }
`;

const Overlay = styled.div<{ $open: boolean }>`
  position: fixed;
  inset: 0;
  z-index: 999;
  pointer-events: ${(p) => (p.$open ? 'auto' : 'none')};
`;

const Backdrop = styled.div<{ $open: boolean }>`
  position: absolute;
  inset: 0;
  /* Soft scrim — light enough that the wallpaper still shows through the
     liquid-glass panel for the proper refractive feel. */
  background: rgba(0, 0, 0, 0.18);
  ${(p) => fluidScrim(p.$open) /* [FLUID-2 2026-10-12] fades in, out a little quicker */}
`;

/* [LIQUID-GLASS 2026-11-02, user] "the notification section … should be based on the new layout … like the curve from the
   edges that you have added in the bin and in the app alert": the same floating frosted sheet (styles/liquidGlass.ts),
   10 px clear of the window's top, right and bottom edges, smooth round corners; hidden 24 px past its own width. */
const Panel = styled.div<{ $open: boolean }>`
  position: absolute;
  top: 10px;
  right: 10px;
  bottom: 10px;
  width: 420px;
  max-width: calc(90vw / var(--app-zoom, 1)); /* [APP-FIT 2026-10-26] the window in page px */
  display: flex;
  flex-direction: column;
  overflow: hidden;
  color: ${(p) => p.theme.text};
  ${lgSurface(34)}
  ${lgSquircle(26)}
  /* Shadow only when open — otherwise the off-screen panel's drop shadow
     bleeds back into the right edge of the page. */
  box-shadow: ${(p) => (p.$open ? lgSurfaceShadow(p) : 'none')};

  /* [PHONE-LAYOUT 2026-10-22] Phones: one full-width sheet, like App Alerts and the Bin (90vw left a thin strip of
     page beside it and each drawer had its own width). The header's close button closes it.
     [LIQUID-GLASS 2026-11-02] It floats 8 px clear of the screen's edges, with 22 px corners. */
  @media (max-width: 768px) {
    top: 8px;
    right: 8px;
    bottom: 8px;
    width: calc(100% - 16px);
    max-width: none;
    border-radius: 22px;
  }

  /* [FLUID-2 2026-10-12, user] Slides in on the spring (0.34 s), out quicker (0.22 s) — components/Fluid.tsx. */
  ${(p) => fluidSheet(p.$open, 'translateX(calc(100% + 24px))')}
`;

/* [LIQUID-GLASS 2026-11-02, user] The green band is gone, like the other two panels: the title sits on the glass over a
   hairline, and a soft green glow from the top edge keeps the bell's brand colour. */
const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 14px 12px 20px;
  flex-shrink: 0;
  border-bottom: 1px solid ${lgHair};
  background: ${(p) => lgTopGlow(p.theme.colorScheme === 'dark' ? 'rgba(74,205,139,0.16)' : 'rgba(16,185,129,0.12)')};

  /* [PHONE-LAYOUT 2026-10-22] Phones: the same header height with the bigger close button. */
  @media (max-width: 768px) {
    padding: 12px 12px 12px 16px;
  }
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  color: ${(p) => p.theme.text};
`;

const PanelTitle = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: ${(p) => p.theme.text};
`;

/* [LIQUID-GLASS 2026-11-02] A small glass capsule. */
const Badge = styled.span`
  background: ${lgControlBg};
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.28), 0 0 0 1px ${lgHair};
  color: ${(p) => p.theme.text};
  font-size: 11px;
  font-weight: 700;
  min-width: 22px;
  height: 22px;
  border-radius: 999px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 7px;
`;

/* [LIQUID-GLASS 2026-11-02] A round glass button that squishes and glows where it is pressed. */
const CloseBtn = styled.button.attrs<LgPressProps>(LG_PRESS)`
  ${lgControl}
  ${lgPress}
  position: relative;
  border: none;
  color: ${(p) => p.theme.text};
  width: 34px;
  height: 34px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 13px;

  /* [PHONE-LAYOUT 2026-10-22] Phones: a 36px tap target. */
  @media (max-width: 768px) {
    width: 36px;
    height: 36px;
    font-size: 15px;
  }
`;

const PanelBody = styled.div<{ $land: boolean }>`
  flex: 1;
  overflow-y: auto;
  /* Clip the card as it swipes out to the right — without this the auto
     overflow-x (implied by overflow-y) would flash a horizontal scrollbar. */
  overflow-x: hidden;
  padding: 14px 14px 16px;
  /* [LIQUID-GLASS 2026-11-02] Cards fade softly as they scroll up under the header. */
  -webkit-mask-image: linear-gradient(180deg, transparent 0, #000 14px);
  mask-image: linear-gradient(180deg, transparent 0, #000 14px);
  /* [PHONE-LAYOUT 2026-10-22] Phones: 12px around the cards so they get the width. */
  @media (max-width: 768px) {
    padding: 12px;
  }
  /* [FLUID-2 2026-10-12, user] Right after opening (and when the cards land) they glide in from the right, one after
     another, as the panel settles. Only in that short window (useLandWindow), so a card's own swipe-out later is
     never overridden. */
  ${(p) => (p.$land ? fluidCascade('> *', 0, 20, 8, 'softSlide') /* [FLUID-8 2026-10-12] soft: readable at once */ : '')}
`;

const EmptyMsg = styled.div`
  color: ${(p) => p.theme.muted};
  font-size: 14px;
  text-align: center;
  padding: 40px 0;
`;

/* ── Card styles ── */

/* [LIQUID-GLASS 2026-11-02, user] A glass card on the glass sheet (light top edge, smooth corners), like the App Alerts
   and Bin cards. No blur of its own (the sheet already blurs the page). A hover brightens it a little. The swipe-out
   when a card is done stays. */
const Card = styled.div<{ $type: 'refund' | 'dispute'; $exiting?: boolean }>`
  position: relative;
  isolation: isolate;
  margin-bottom: 12px;
  overflow: hidden;
  ${lgCard}
  ${lgSquircle(22)}
  transition: ${transition.hover}, transform ${duration.micro} ${easing.standard};
  & > * { position: relative; z-index: 1; }

  &:hover {
    background-color: ${lgControlHover};
  }

  ${(p) => p.$exiting && css`
    animation: ${cardSwipeOut} 0.5s ${easing.emphasized} forwards;
    pointer-events: none;
  `}
`;

/* [LIQUID-GLASS 2026-11-02, user] Was a 3 px red / amber line on top of the card: now a soft glow of that colour from the
   card's top edge, behind the words. "&&" so it wins over the card's own rule that lifts its children (z-index 1). */
const CardTopBar = styled.div<{ $type: 'refund' | 'dispute' }>`
  && {
    position: absolute;
    inset: 0 0 auto 0;
    height: 96px;
    z-index: -1;
    pointer-events: none;
    background: ${(p) => lgTopGlow(p.$type === 'refund'
      ? (p.theme.colorScheme === 'dark' ? 'rgba(248,113,113,0.20)' : 'rgba(220,38,38,0.12)')
      : (p.theme.colorScheme === 'dark' ? 'rgba(251,191,36,0.16)' : 'rgba(217,119,6,0.12)'))};
  }
`;

const CardHeader = styled.div<{ $type: 'refund' | 'dispute' }>`
  text-align: center;
  padding: 10px 16px 8px;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: ${(p) => {
    if (p.theme.colorScheme === 'dark') {
      // [LIQUID-GLASS 2026-11-02] the brighter inks App Alerts uses: the old dim red / brown sat at ~2 : 1 on the glass
      return p.$type === 'refund' ? '#fca5a5' : '#fcd34d';
    }
    // Light mode: full-opacity, slightly darker shades so labels are crisp
    // against the near-white card background.
    return p.$type === 'refund' ? '#b91c1c' : '#a16207';
  }};
  border-bottom: 1px solid ${lgHair};
`;

const CardBody = styled.div`
  padding: 12px 16px 6px;
`;

const Row = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  padding: 4px 0;
  font-size: 13px;

  /* [PHONE-LAYOUT 2026-10-22] Phones: room between a label and a value that wraps (see Value). */
  @media (max-width: 768px) {
    gap: 12px;
  }
`;

const Label = styled.span`
  color: ${(p) => p.theme.muted};
  font-weight: 500;
`;

const Value = styled.span`
  color: ${(p) => p.theme.text};
  font-weight: 600;
  text-align: right;
  max-width: 60%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  /* [PHONE-LAYOUT 2026-10-22] Phones: a long website, email or reason wraps onto a second line instead of
     ending in "..." (there is no hover on a phone to read the rest). */
  @media (max-width: 768px) {
    max-width: 64%;
    white-space: normal;
    overflow-wrap: anywhere;
    text-overflow: clip;
  }
`;

// [LIQUID-GLASS 2026-11-02] dark: a softer, brighter red / amber that reads on the glass (light keeps its colours)
const AmountValue = styled(Value)<{ $type: 'refund' | 'dispute' }>`
  color: ${(p) => (p.theme.colorScheme === 'dark'
    ? (p.$type === 'refund' ? '#f87171' : '#fbbf24')
    : (p.$type === 'refund' ? 'rgba(220, 38, 38, 0.8)' : 'rgba(180, 100, 10, 0.85)'))};
`;

const CardActions = styled.div`
  display: flex;
  gap: 10px;
  padding: 10px 14px 12px;
  border-top: 1px solid ${lgHair};
`;

// [SUGGEST-CARD 2026-09-16] The PM / Sales name dropdowns are gone (user: Accept + Cancel only).
// The card now shows what the case's payment says: the matched payment, how it was mapped, and
// the suggested Team + Sales/PM Name — or, when there is no usable answer, the reason.
const SuggestBox = styled.div<{ $warn: boolean }>`
  margin: 2px 16px 12px;
  padding: 10px 12px 8px;
  border-radius: 14px; /* [LIQUID-GLASS 2026-11-02] rounder, like the glass cards */
  background: ${(p) => (p.$warn
    ? (p.theme.colorScheme === 'dark' ? 'rgba(217,119,6,0.12)' : 'rgba(217,119,6,0.07)')
    : (p.theme.colorScheme === 'dark' ? 'rgba(14,126,78,0.16)' : 'rgba(26,127,100,0.07)'))};
  border: 1px solid ${(p) => (p.$warn
    ? 'rgba(217,119,6,0.35)'
    : (p.theme.colorScheme === 'dark' ? 'rgba(74,205,139,0.28)' : 'rgba(26,127,100,0.28)'))};
`;

const SuggestTitle = styled.div`
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: ${(p) => p.theme.muted};
  margin-bottom: 4px;
`;

const SuggestLine = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 0 2px;
  font-size: 14px;
  font-weight: 700;
  color: ${(p) => p.theme.text};
`;

const TeamTag = styled.span<{ $variant: 'pm' | 'sales' }>`
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 2px 8px;
  border-radius: 999px;
  color: ${(p) => {
    if (p.theme.colorScheme === 'dark') return p.$variant === 'pm' ? '#818cf8' : '#fbbf24';
    return p.$variant === 'pm' ? '#4338ca' : '#b45309';
  }};
  background: ${(p) => (p.$variant === 'pm' ? 'rgba(99,102,241,0.14)' : 'rgba(245,158,11,0.16)')};
`;

const SuggestSource = styled.div`
  font-size: 12px;
  color: ${(p) => p.theme.muted};
`;

const ReasonText = styled.div`
  font-size: 13px;
  font-weight: 600;
  padding: 4px 0 2px;
  color: ${(p) => (p.theme.colorScheme === 'dark' ? '#fbbf24' : '#a16207')};
`;

// [NOTIF-DEPARTED 2026-10-08, user] One short line when the person is no longer an active HubSpot member of
// the payment's side (left the company, or sits on the other team) — the reason line's amber, a size smaller.
const NoteText = styled(ReasonText)`
  font-size: 12px;
  padding: 4px 0 0;
`;

// [NOTIF-DEPARTED 2026-10-08, user] "Not sure whether he is from Sales or PM — ask me": two chips drawn like
// the TeamTag pill, outlined until picked; the picked one takes the tag's filled colours. Accept waits for it.
const TeamChoice = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 0 2px;
  font-size: 13px;
`;

const TeamChip = styled.button<{ $variant: 'pm' | 'sales'; $on: boolean }>`
  font-family: inherit;
  font-size: 11px;
  font-weight: 700;
  line-height: 16px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 2px 12px;
  border-radius: 999px;
  cursor: pointer;
  border: 1px solid ${(p) => (p.$variant === 'pm' ? 'rgba(99,102,241,0.45)' : 'rgba(245,158,11,0.55)')};
  background: ${(p) => (!p.$on ? 'transparent' : p.$variant === 'pm' ? 'rgba(99,102,241,0.14)' : 'rgba(245,158,11,0.16)')};
  color: ${(p) => {
    if (!p.$on) return p.theme.muted;
    if (p.theme.colorScheme === 'dark') return p.$variant === 'pm' ? '#818cf8' : '#fbbf24';
    return p.$variant === 'pm' ? '#4338ca' : '#b45309';
  }};
  transition: background ${duration.micro} ${easing.standard}, color ${duration.micro} ${easing.standard};

  &:hover:not(:disabled) {
    color: ${(p) => {
      if (p.theme.colorScheme === 'dark') return p.$variant === 'pm' ? '#818cf8' : '#fbbf24';
      return p.$variant === 'pm' ? '#4338ca' : '#b45309';
    }};
  }
  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }

  /* [PHONE-LAYOUT 2026-10-22] Phones: a chip a finger can hit (about 36px tall). */
  @media (max-width: 768px) {
    padding: 9px 16px;
  }
`;

// [BUTTON-STYLE 2026-09-30] Accept ($primary) = the green primary, Cancel = the secondary; flex stays (layout).
// [LIQUID-GLASS 2026-11-02] Glass capsules (squish + press light).
const ActionBtn = styled.button.attrs<LgPressProps>(LG_PRESS)<{ $primary?: boolean }>`
  ${(p) => (p.$primary ? lgButtonPrimary : lgButtonSecondary)}
  flex: 1;
  /* [PHONE-LAYOUT 2026-10-22] Phones: 40px tall tap targets. */
  @media (max-width: 768px) {
    height: 40px;
  }
`;

interface Notification {
  id: number;
  type: 'refund' | 'dispute';
  website: string;
  amount: number;
  date: string;
  email: string;
  payment_date: string;
  account: string;
  client: string;
  month_label: string;
  dispute_status?: string;
  dispute_reason?: string;
  // [SUGGEST-CARD 2026-09-16] research from GET /api/notifications/pending
  saved_team: string | null;
  saved_name: string | null;
  payment: { id: number; date: string; amount: number; mapped: string } | null;
  // [NOTIF-DEPARTED 2026-10-08] team null = the card asks Sales / PM; `note` = the person is no longer an
  // active HubSpot member of the payment's side (server: services/caseSuggestion.ts resolveOffRoster).
  suggestion: {
    team: 'PM' | 'Sales' | null;
    name: string;
    source: 'deal' | 'pm_name';
    inactive?: boolean;
    note?: string;
  } | null;
  reason: string | null;
}

interface Props {
  open: boolean;
  onClose: () => void;
}

function fmtDate(d: string | null): string {
  if (!d) return '\u2014';
  return MONTH_SHORT_DAY_YEAR_UTC_FMT.format(new Date(d));
}

function fmtCurrency(n: number): string {
  return `$${MONEY_FMT.format(n)}`;
}

const NotificationPanel: React.FC<Props> = ({ open, onClose }) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(false);
  // [PANEL-KEEP 2026-10-13, user] "make the fix" — this panel used to throw its list away on every opening and show skeleton
  // cards until the server answered again. Once a list has loaded, a reopening shows it AT ONCE and refreshes it behind
  // (the skeleton only before the very first list), like App Alerts. A card acted on in between is re-checked by the server.
  const loadedRef = useRef(false);
  const [loaded, setLoaded] = useState(false);
  const [assigning, setAssigning] = useState<string | null>(null);
  // Keys mid swipe-out. The card stays mounted until its animation ends, then
  // onAnimationEnd removes it from the list.
  const [exiting, setExiting] = useState<Set<string>>(new Set());
  // [NOTIF-DEPARTED 2026-10-08] The Sales / PM pick on cards that ask for the team, by card key.
  const [picked, setPicked] = useState<Record<string, 'PM' | 'Sales'>>({});

  useEffect(() => {
    if (!open) return;
    setPicked({}); // [NOTIF-DEPARTED 2026-10-08] every opening starts with no pick
    setLoading(true);
    fetch('/api/notifications/pending')
      .then((r) => r.json())
      .then((data) => { setNotifications(Array.isArray(data) ? data : []); loadedRef.current = true; setLoaded(true); })
      .catch(() => { if (!loadedRef.current) setNotifications([]); }) // [PANEL-KEEP] a failed refresh keeps the last list
      .finally(() => setLoading(false));
  }, [open]);

  // [SUGGEST-CARD 2026-09-16] Accept saves the suggested Team + Sales/PM Name (the server re-runs
  // the research and refuses if it changed); Cancel removes the card for good and touches nothing
  // else. Either way the success toast only shows after the server confirmed the write.
  const handleAction = async (n: Notification, action: 'accept' | 'cancel') => {
    const key = `${n.type}-${n.id}`;
    if (assigning === key || exiting.has(key)) return;
    if (action === 'accept' && !n.suggestion) return;
    // [NOTIF-DEPARTED 2026-10-08, user] The team to save: the card's own, or the admin's Sales / PM pick when
    // the card asked for it. The name is always the server's suggestion.
    const team = n.suggestion?.team ?? picked[key];
    if (action === 'accept' && !team) return;
    setAssigning(key);
    try {
      // [ACCEPT-REFRESH 2026-09-17, user] Through the shared axios `api` (was a bare fetch): the
      // write now clears the client GET cache and fires the on-write bus like every other save.
      // Both calls resolve only on a 2xx, i.e. after the server's UPDATE has returned.
      if (action === 'accept') {
        await acceptNotificationSuggestion({ id: n.id, type: n.type, team: team!, name: n.suggestion!.name });
      } else {
        await cancelNotificationCard({ id: n.id, type: n.type });
      }
      // [ACCEPT-REFRESH 2026-09-17, user] The server's data-version watcher cannot see a Team /
      // Sales-PM Name change (see notifyDataChanged), so tell the open pages ourselves: the
      // Refunds / Disputes table reloads silently, and so does Application Logs (Cancel writes
      // only a log entry, so only the logs).
      notifyDataChanged(action === 'accept'
        ? [n.type === 'refund' ? 'refunds' : 'disputes', 'activityLogs']
        : ['activityLogs']);
      // Kick off the swipe-out; the card is removed from the list in
      // onAnimationEnd once the glide-and-collapse finishes.
      setExiting((prev) => new Set(prev).add(key));
      toastSuccess(action === 'accept'
        ? `Saved: ${team} · ${n.suggestion!.name}` // [NOTIF-DEPARTED 2026-10-08] the saved team, picked or not
        : 'Removed from notifications');
    } catch (err: any) {
      console.error(`Failed to ${action}:`, err);
      toastError(err?.response?.data?.error || (action === 'accept' ? 'Failed to save' : 'Failed to remove'));
    } finally {
      setAssigning(null);
    }
  };

  // [FLUID-2 2026-10-12] The window in which the cards glide in: the panel opening, then the cards landing.
  // [PANEL-KEEP] a refresh behind the shown list never replays the glide (same key once a list has loaded)
  const land = useLandWindow(open, loaded ? 'list' : loading ? 'loading' : 'ready');

  const handleCardExited = (n: Notification, key: string) => {
    setNotifications((prev) => prev.filter((x) => !(x.type === n.type && x.id === n.id)));
    setExiting((prev) => {
      const next = new Set(prev);
      next.delete(key);
      return next;
    });
  };

  // Portal to <body> so the fixed drawer always anchors to the viewport,
  // regardless of transformed/filtered ancestors.
  // [AUTO-REFRESH 2026-08-03] data-reload-block only while open — this panel stays mounted.
  return createPortal(
    <Overlay $open={open} data-reload-block={open ? '' : undefined}>
      <Backdrop $open={open} onClick={onClose} />
      <Panel $open={open}>
        <PanelHeader>
          <HeaderLeft>
            <PanelTitle>Notifications</PanelTitle>
            <Badge>{notifications.length}</Badge>
          </HeaderLeft>
          <CloseBtn onClick={onClose}>
            <i className="fas fa-times"></i>
          </CloseBtn>
        </PanelHeader>
        <PanelBody $land={land}>
          {loading && !loaded ? ( /* [PANEL-KEEP] skeleton only before the first list */
            // Skeleton mirrors the real notification Card (top bar + header + detail
            // rows) instead of plain "Loading..." text.
            <>
              {Array.from({ length: 3 }).map((_, i) => (
                <Card key={i} $type="refund" $exiting={false} aria-busy="true">
                  <CardTopBar $type="refund" />
                  <CardHeader $type="refund"><SkeletonBox $w="110px" $h="11px" /></CardHeader>
                  <CardBody>
                    {Array.from({ length: 4 }).map((_, r) => (
                      <Row key={r}>
                        <SkeletonBox $w="78px" $h="10px" />
                        <SkeletonBox $w="120px" $h="10px" />
                      </Row>
                    ))}
                  </CardBody>
                </Card>
              ))}
            </>
          ) : notifications.length === 0 ? (
            <EmptyMsg>No pending cases to assign</EmptyMsg>
          ) : (
            notifications.map((n) => {
              const key = `${n.type}-${n.id}`;
              const isRefund = n.type === 'refund';
              const isExiting = exiting.has(key);
              return (
                <Card
                  key={key}
                  $type={n.type}
                  $exiting={isExiting}
                  onAnimationEnd={() => { if (isExiting) handleCardExited(n, key); }}
                >
                  <CardTopBar $type={n.type} />
                  <CardHeader $type={n.type}>
                    {isRefund ? 'REFUND CASE' : 'DISPUTE CASE'}
                  </CardHeader>
                  <CardBody>
                    <Row>
                      <Label>{isRefund ? 'Refund Date:' : 'Dispute Date:'}</Label>
                      <Value>{fmtDate(n.date)}</Value>
                    </Row>
                    <Row>
                      <Label>Website:</Label>
                      <Value>{n.website || '\u2014'}</Value>
                    </Row>
                    <Row>
                      <Label>Client Email:</Label>
                      <Value>{n.email || '\u2014'}</Value>
                    </Row>
                    <Row>
                      <Label>Payment Date:</Label>
                      <Value>{fmtDate(n.payment_date)}</Value>
                    </Row>
                    <Row>
                      <Label>{isRefund ? 'Refund Amount:' : 'Dispute Amount:'}</Label>
                      <AmountValue $type={n.type}>{fmtCurrency(Number(n.amount) || 0)}</AmountValue>
                    </Row>
                    <Row>
                      <Label>Account:</Label>
                      <Value>{n.account || '\u2014'}</Value>
                    </Row>
                    <Row>
                      <Label>Month:</Label>
                      <Value>{n.month_label || '\u2014'}</Value>
                    </Row>
                    {!isRefund && (
                      <>
                        <Row>
                          <Label>Status:</Label>
                          <Value>{n.dispute_status || '\u2014'}</Value>
                        </Row>
                        <Row>
                          <Label>Reason:</Label>
                          <Value>{n.dispute_reason || '\u2014'}</Value>
                        </Row>
                      </>
                    )}
                  </CardBody>
                  {/* [SUGGEST-CARD 2026-09-16] What the case's payment says. */}
                  <SuggestBox $warn={!n.suggestion}>
                    <SuggestTitle>From the payment</SuggestTitle>
                    <Row>
                      <Label>Matched payment:</Label>
                      <Value>
                        {n.payment ? `${fmtDate(n.payment.date)} · ${fmtCurrency(Number(n.payment.amount) || 0)}` : '—'}
                      </Value>
                    </Row>
                    {n.payment && (
                      <Row>
                        <Label>Mapped as:</Label>
                        <Value title={n.payment.mapped}>{n.payment.mapped}</Value>
                      </Row>
                    )}
                    {n.saved_team && (
                      <Row>
                        <Label>Saved now:</Label>
                        <Value>{`${n.saved_team} · ${n.saved_name || '—'}`}</Value>
                      </Row>
                    )}
                    {n.suggestion ? (
                      <>
                        <SuggestLine>
                          {/* [NOTIF-DEPARTED 2026-10-08] no tag while the card asks for the team */}
                          {n.suggestion.team && (
                            <TeamTag $variant={n.suggestion.team === 'PM' ? 'pm' : 'sales'}>{n.suggestion.team}</TeamTag>
                          )}
                          {n.suggestion.name}
                        </SuggestLine>
                        <SuggestSource>
                          {n.suggestion.source === 'deal' ? 'Deal owner on this payment' : 'PM Name on this payment'}
                        </SuggestSource>
                        {/* [NOTIF-DEPARTED 2026-10-08, user] the person left (or is on the other team) — say so, don't block */}
                        {n.suggestion.note && <NoteText>{n.suggestion.note}</NoteText>}
                        {/* [NOTIF-DEPARTED 2026-10-08, user] team not certain → the admin picks Sales or PM here */}
                        {n.suggestion.team === null && (
                          <TeamChoice role="radiogroup" aria-label="Team">
                            <Label>Team:</Label>
                            {(['Sales', 'PM'] as const).map((t) => (
                              <TeamChip
                                key={t}
                                type="button"
                                role="radio"
                                aria-checked={picked[key] === t}
                                $variant={t === 'PM' ? 'pm' : 'sales'}
                                $on={picked[key] === t}
                                disabled={assigning === key || isExiting}
                                onClick={() => setPicked((prev) => ({ ...prev, [key]: t }))}
                              >
                                {t}
                              </TeamChip>
                            ))}
                          </TeamChoice>
                        )}
                      </>
                    ) : (
                      <ReasonText>{n.reason || 'No suggestion for this case'}</ReasonText>
                    )}
                  </SuggestBox>
                  <CardActions>
                    <ActionBtn
                      disabled={assigning === key || isExiting}
                      onClick={() => handleAction(n, 'cancel')}
                    >
                      Cancel
                    </ActionBtn>
                    {n.suggestion && (
                      <ActionBtn
                        $primary
                        // [NOTIF-DEPARTED 2026-10-08] a card that asks for the team waits for the Sales / PM pick
                        disabled={assigning === key || isExiting || (n.suggestion.team === null && !picked[key])}
                        title={n.suggestion.team === null && !picked[key] ? 'Pick Sales or PM first' : undefined}
                        onClick={() => handleAction(n, 'accept')}
                      >
                        Accept
                      </ActionBtn>
                    )}
                  </CardActions>
                </Card>
              );
            })
          )}
        </PanelBody>
      </Panel>
    </Overlay>,
    document.body
  );
};

export default NotificationPanel;
